import { useState, useCallback } from "react";
import { ApprovalRequest, TraceabilityData } from "../types";
import { apiService } from "../services/api";

interface ApprovalDetails {
  id: string;
  prUrl: string;
  diff: string;
  jiraTicket?: any;
  pseudocodeRef?: string;
  testResults?: any;
  comments: Array<{
    id: string;
    author: string;
    content: string;
    createdAt: string;
  }>;
}

export const useApprovalActions = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchApprovals = useCallback(async (): Promise<ApprovalRequest[]> => {
    try {
      setLoading(true);
      setError(null);

      const data = await apiService.getApprovalQueue();

      // Convert date strings to Date objects
      return data.map((approval: any) => ({
        ...approval,
        createdAt: new Date(approval.createdAt),
      }));
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch approvals";
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchApprovalDetails = useCallback(
    async (approvalId: string): Promise<ApprovalDetails> => {
      try {
        setLoading(true);
        setError(null);

        const data = await apiService.getApprovalDetails(approvalId);
        return data;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to fetch approval details";
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const fetchTraceability = useCallback(
    async (approvalId: string): Promise<TraceabilityData> => {
      try {
        setLoading(true);
        setError(null);

        const data = await apiService.getApprovalTraceability(approvalId);

        // Ensure proper typing by creating a properly typed object
        const typedData: TraceabilityData = {
          spec: {
            status: data.spec.status as
              | "completed"
              | "in_progress"
              | "pending"
              | "failed",
            timestamp: data.spec.timestamp,
            url: data.spec.url,
          },
          pseudocode: {
            status: data.pseudocode.status as
              | "completed"
              | "in_progress"
              | "pending"
              | "failed",
            timestamp: data.pseudocode.timestamp,
            url: data.pseudocode.url,
          },
          tasks: {
            status: data.tasks.status as
              | "completed"
              | "in_progress"
              | "pending"
              | "failed",
            timestamp: data.tasks.timestamp,
            url: data.tasks.url,
          },
          workspace: {
            status: data.workspace.status as
              | "completed"
              | "in_progress"
              | "pending"
              | "failed",
            timestamp: data.workspace.timestamp,
            url: data.code.url,
          },
          pr: {
            status: data.pr.status as
              | "completed"
              | "in_progress"
              | "pending"
              | "failed",
            timestamp: data.pr.timestamp,
            url: data.pr.url,
          },
          deployment: {
            status: data.deployment.status as
              | "completed"
              | "in_progress"
              | "pending"
              | "failed",
            timestamp: data.deployment.timestamp,
            url: data.deployment.url,
          },
        };

        return typedData;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to fetch traceability";
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const approveRequest = useCallback(
    async (approvalId: string, comment?: string): Promise<void> => {
      try {
        setLoading(true);
        setError(null);

        await apiService.approveRequest(approvalId, comment);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to approve request";
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const rejectRequest = useCallback(
    async (approvalId: string, comment: string): Promise<void> => {
      try {
        setLoading(true);
        setError(null);

        await apiService.rejectRequest(approvalId, comment);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to reject request";
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const addComment = useCallback(
    async (approvalId: string, comment: string): Promise<void> => {
      try {
        setLoading(true);
        setError(null);

        await apiService.addApprovalComment(approvalId, comment);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to add comment";
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const batchApprove = useCallback(
    async (approvalIds: string[], comment?: string): Promise<void> => {
      try {
        setLoading(true);
        setError(null);

        await apiService.batchApprove(approvalIds, comment);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to batch approve";
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const batchReject = useCallback(
    async (approvalIds: string[], comment: string): Promise<void> => {
      try {
        setLoading(true);
        setError(null);

        await apiService.batchReject(approvalIds, comment);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to batch reject";
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    loading,
    error,
    fetchApprovals,
    fetchApprovalDetails,
    fetchTraceability,
    approveRequest,
    rejectRequest,
    addComment,
    batchApprove,
    batchReject,
  };
};
